import React, { useEffect, useState } from 'react';
import { mediaKitAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';

const emptyKit = {
  creatorName: '', tagline: '', bio: '', niche: '', email: '', phone: '', website: '',
  ytSubscribers: '', ytAvgViews: '', ytEngagementRate: '', ytChannelUrl: '',
  igFollowers: '', igAvgReach: '', igEngagementRate: '', igHandle: '',
  twFollowers: '', twHandle: '',
  liFollowers: '', liProfileUrl: '',
  fbFollowers: '',
  audienceAgeRange: '', audienceGender: '', audienceTopCities: '', audienceTopCountry: '',
  pastBrands: '', contentCategories: '', startingRate: '',
};

const TABS = ['Profile', 'Platforms', 'Audience', 'Brands & Rates'];

export default function MediaKitPage() {
  const { user } = useAuth();
  const [kit, setKit]           = useState(emptyKit);
  const [loading, setLoading]   = useState(true);
  const [saving, setSaving]     = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [saved, setSaved]       = useState(false);
  const [tab, setTab]           = useState(0);

  useEffect(() => {
    mediaKitAPI.get().then(res => {
      if (!res.data.empty) {
        const d = res.data;
        setKit({
          creatorName:       d.creatorName       || '',
          tagline:           d.tagline           || '',
          bio:               d.bio               || '',
          niche:             d.niche             || '',
          email:             d.email             || '',
          phone:             d.phone             || '',
          website:           d.website           || '',
          ytSubscribers:     d.ytSubscribers     || '',
          ytAvgViews:        d.ytAvgViews        || '',
          ytEngagementRate:  d.ytEngagementRate  || '',
          ytChannelUrl:      d.ytChannelUrl      || '',
          igFollowers:       d.igFollowers       || '',
          igAvgReach:        d.igAvgReach        || '',
          igEngagementRate:  d.igEngagementRate  || '',
          igHandle:          d.igHandle          || '',
          twFollowers:       d.twFollowers       || '',
          twHandle:          d.twHandle          || '',
          liFollowers:       d.liFollowers       || '',
          liProfileUrl:      d.liProfileUrl      || '',
          fbFollowers:       d.fbFollowers       || '',
          audienceAgeRange:  d.audienceAgeRange  || '',
          audienceGender:    d.audienceGender    || '',
          audienceTopCities: d.audienceTopCities || '',
          audienceTopCountry:d.audienceTopCountry|| '',
          pastBrands:        d.pastBrands        || '',
          contentCategories: d.contentCategories || '',
          startingRate:      d.startingRate      || '',
        });
      } else {
        // Pre-fill from user profile
        setKit(k => ({
          ...k,
          creatorName: user?.businessName || user?.name || '',
          email:       user?.email        || '',
          phone:       user?.phone        || '',
        }));
      }
    }).catch(() => {}).finally(() => setLoading(false));
  }, [user]);

  const f = (updates) => setKit(k => ({ ...k, ...updates }));

  const handleSave = async () => {
    setSaving(true);
    setSaved(false);
    try {
      await mediaKitAPI.save(kit);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch { alert('Failed to save'); }
    setSaving(false);
  };

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const res = await mediaKitAPI.downloadPdf();
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a   = document.createElement('a');
      a.href    = url;
      a.download = 'media-kit.pdf';
      a.click();
      window.URL.revokeObjectURL(url);
    } catch { alert('Save your media kit first before downloading PDF'); }
    setDownloading(false);
  };

  if (loading) return <div style={{ padding: '2rem', color: '#888' }}>Loading…</div>;

  return (
    <div style={{ padding: '2rem', maxWidth: 860, margin: '0 auto' }}>

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '1rem' }}>
        <div>
          <h1 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 800, color: '#1a1a2e' }}>Media Kit Builder 📁</h1>
          <p style={{ margin: '0.25rem 0 0', color: '#888', fontSize: '0.875rem' }}>
            Build your creator portfolio — share with brands to land deals
          </p>
        </div>
        <div style={{ display: 'flex', gap: '0.75rem' }}>
          <button onClick={handleSave} disabled={saving} style={primaryBtn}>
            {saving ? 'Saving…' : saved ? '✅ Saved!' : '💾 Save Kit'}
          </button>
          <button onClick={handleDownload} disabled={downloading} style={outlineBtn}>
            {downloading ? '⏳' : '⬇️ Download PDF'}
          </button>
        </div>
      </div>

      {/* Tip */}
      <div style={{ background: '#e8f5e9', border: '1px solid #c8ebd8', borderRadius: 10, padding: '0.75rem 1rem', marginBottom: '1.25rem', fontSize: '0.82rem', color: '#2e7d32' }}>
        💡 <b>Tip:</b> Fill all sections and download as PDF. Share it directly with brands via email or WhatsApp.
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: '0.25rem', marginBottom: '1.25rem', background: '#f5f5f5', padding: '0.3rem', borderRadius: 12 }}>
        {TABS.map((t, i) => (
          <button key={t} onClick={() => setTab(i)} style={{
            flex: 1, padding: '0.55rem', border: 'none', borderRadius: 9, cursor: 'pointer',
            fontWeight: 700, fontSize: '0.82rem',
            background: tab === i ? 'white' : 'transparent',
            color: tab === i ? '#1a7a4a' : '#888',
            boxShadow: tab === i ? '0 1px 4px rgba(0,0,0,0.08)' : 'none',
            transition: 'all 0.15s',
          }}>{t}</button>
        ))}
      </div>

      {/* Tab Content */}
      <div style={{ background: 'white', borderRadius: 16, padding: '1.5rem', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>

        {/* Tab 0: Profile */}
        {tab === 0 && (
          <div style={grid2}>
            <div style={sectionLabel}>👤 Creator Profile</div>
            <label style={{ ...labelStyle, gridColumn: '1/-1' }}>
              Full Name / Creator Name
              <input value={kit.creatorName} onChange={e => f({ creatorName: e.target.value })} style={input} placeholder="e.g. Raju Tambe" />
            </label>
            <label style={{ ...labelStyle, gridColumn: '1/-1' }}>
              Tagline
              <input value={kit.tagline} onChange={e => f({ tagline: e.target.value })} style={input} placeholder="e.g. Tech & Lifestyle Creator | 500K+ Subscribers | Mumbai" />
            </label>
            <label style={{ ...labelStyle, gridColumn: '1/-1' }}>
              Bio (tell brands about yourself)
              <textarea value={kit.bio} onChange={e => f({ bio: e.target.value })}
                style={{ ...input, height: 100, resize: 'vertical' }}
                placeholder="I'm a tech and lifestyle creator from Mumbai with 5+ years of content creation experience. I create authentic, engaging content that connects brands with young Indian audiences..." />
            </label>
            <label style={labelStyle}>
              Niche / Categories
              <input value={kit.niche} onChange={e => f({ niche: e.target.value })} style={input} placeholder="e.g. Tech, Gaming, Lifestyle" />
            </label>
            <label style={labelStyle}>
              Email
              <input value={kit.email} onChange={e => f({ email: e.target.value })} style={input} type="email" placeholder="your@email.com" />
            </label>
            <label style={labelStyle}>
              Phone
              <input value={kit.phone} onChange={e => f({ phone: e.target.value })} style={input} placeholder="+91 9876543210" />
            </label>
            <label style={labelStyle}>
              Website / Portfolio
              <input value={kit.website} onChange={e => f({ website: e.target.value })} style={input} placeholder="https://yourwebsite.com" />
            </label>
          </div>
        )}

        {/* Tab 1: Platforms */}
        {tab === 1 && (
          <div>
            {/* YouTube */}
            <div style={platformHeader('#ff0000')}>▶ YouTube</div>
            <div style={{ ...grid2, marginBottom: '1.5rem' }}>
              <label style={labelStyle}>Subscribers
                <input value={kit.ytSubscribers} onChange={e => f({ ytSubscribers: e.target.value })} style={input} type="number" placeholder="500000" />
              </label>
              <label style={labelStyle}>Avg Views per Video
                <input value={kit.ytAvgViews} onChange={e => f({ ytAvgViews: e.target.value })} style={input} type="number" placeholder="80000" />
              </label>
              <label style={labelStyle}>Engagement Rate
                <input value={kit.ytEngagementRate} onChange={e => f({ ytEngagementRate: e.target.value })} style={input} placeholder="4.5%" />
              </label>
              <label style={labelStyle}>Channel URL
                <input value={kit.ytChannelUrl} onChange={e => f({ ytChannelUrl: e.target.value })} style={input} placeholder="https://youtube.com/@channel" />
              </label>
            </div>

            {/* Instagram */}
            <div style={platformHeader('#c13584')}>📸 Instagram</div>
            <div style={{ ...grid2, marginBottom: '1.5rem' }}>
              <label style={labelStyle}>Followers
                <input value={kit.igFollowers} onChange={e => f({ igFollowers: e.target.value })} style={input} type="number" placeholder="120000" />
              </label>
              <label style={labelStyle}>Avg Reach per Post
                <input value={kit.igAvgReach} onChange={e => f({ igAvgReach: e.target.value })} style={input} type="number" placeholder="30000" />
              </label>
              <label style={labelStyle}>Engagement Rate
                <input value={kit.igEngagementRate} onChange={e => f({ igEngagementRate: e.target.value })} style={input} placeholder="5.2%" />
              </label>
              <label style={labelStyle}>Handle
                <input value={kit.igHandle} onChange={e => f({ igHandle: e.target.value })} style={input} placeholder="@yourcreatorhandle" />
              </label>
            </div>

            {/* Twitter/X */}
            <div style={platformHeader('#141414')}>𝕏 Twitter / X</div>
            <div style={{ ...grid2, marginBottom: '1.5rem' }}>
              <label style={labelStyle}>Followers
                <input value={kit.twFollowers} onChange={e => f({ twFollowers: e.target.value })} style={input} type="number" placeholder="25000" />
              </label>
              <label style={labelStyle}>Handle
                <input value={kit.twHandle} onChange={e => f({ twHandle: e.target.value })} style={input} placeholder="@handle" />
              </label>
            </div>

            {/* LinkedIn */}
            <div style={platformHeader('#0077b5')}>in LinkedIn</div>
            <div style={{ ...grid2, marginBottom: '1.5rem' }}>
              <label style={labelStyle}>Followers
                <input value={kit.liFollowers} onChange={e => f({ liFollowers: e.target.value })} style={input} type="number" placeholder="10000" />
              </label>
              <label style={labelStyle}>Profile URL
                <input value={kit.liProfileUrl} onChange={e => f({ liProfileUrl: e.target.value })} style={input} placeholder="https://linkedin.com/in/yourname" />
              </label>
            </div>

            {/* Facebook */}
            <div style={platformHeader('#1877f2')}>👍 Facebook</div>
            <div style={grid2}>
              <label style={labelStyle}>Page Followers
                <input value={kit.fbFollowers} onChange={e => f({ fbFollowers: e.target.value })} style={input} type="number" placeholder="50000" />
              </label>
            </div>
          </div>
        )}

        {/* Tab 2: Audience */}
        {tab === 2 && (
          <div style={grid2}>
            <div style={sectionLabel}>👥 Audience Demographics</div>
            <label style={labelStyle}>
              Age Range
              <input value={kit.audienceAgeRange} onChange={e => f({ audienceAgeRange: e.target.value })} style={input} placeholder="e.g. 18-34 years" />
            </label>
            <label style={labelStyle}>
              Gender Split
              <input value={kit.audienceGender} onChange={e => f({ audienceGender: e.target.value })} style={input} placeholder="e.g. 65% Male, 35% Female" />
            </label>
            <label style={labelStyle}>
              Top Cities
              <input value={kit.audienceTopCities} onChange={e => f({ audienceTopCities: e.target.value })} style={input} placeholder="e.g. Mumbai, Delhi, Bangalore, Hyderabad" />
            </label>
            <label style={labelStyle}>
              Top Country
              <input value={kit.audienceTopCountry} onChange={e => f({ audienceTopCountry: e.target.value })} style={input} placeholder="e.g. India 88%, USA 5%" />
            </label>
          </div>
        )}

        {/* Tab 3: Brands & Rates */}
        {tab === 3 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            <div style={sectionLabel}>🤝 Past Brand Collaborations</div>
            <label style={labelStyle}>
              Brand Names (comma separated)
              <input value={kit.pastBrands} onChange={e => f({ pastBrands: e.target.value })} style={input}
                placeholder="e.g. Noise, boAt, Mamaearth, Nykaa, realme, Flipkart" />
            </label>
            <div style={sectionLabel}>🎬 Content Categories</div>
            <label style={labelStyle}>
              Categories (comma separated)
              <input value={kit.contentCategories} onChange={e => f({ contentCategories: e.target.value })} style={input}
                placeholder="e.g. Unboxing, Product Reviews, Vlogs, Tutorials, Shorts" />
            </label>
            <div style={sectionLabel}>💰 Rate Highlight</div>
            <label style={labelStyle}>
              Starting Rate (shown on PDF to attract brands)
              <input value={kit.startingRate} onChange={e => f({ startingRate: e.target.value })} style={input}
                placeholder="e.g. Starting from ₹15,000 per sponsored integration" />
            </label>
            <div style={{ background: '#fff8e1', border: '1px solid #ffcc02', borderRadius: 10, padding: '0.75rem', fontSize: '0.82rem', color: '#7a5c00' }}>
              💡 For detailed rate card (per platform, per format), use the <b>Rate Card</b> feature (coming next).
            </div>
          </div>
        )}
      </div>

      {/* Bottom save bar */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem', marginTop: '1.25rem' }}>
        {tab > 0 && <button onClick={() => setTab(t => t - 1)} style={outlineBtn}>← Previous</button>}
        {tab < TABS.length - 1
          ? <button onClick={() => setTab(t => t + 1)} style={primaryBtn}>Next →</button>
          : <button onClick={handleSave} disabled={saving} style={primaryBtn}>
              {saving ? 'Saving…' : saved ? '✅ Saved!' : '💾 Save Media Kit'}
            </button>
        }
      </div>
    </div>
  );
}

// Styles
const primaryBtn = { background: '#1a7a4a', color: 'white', border: 'none', borderRadius: 10, padding: '0.6rem 1.2rem', fontWeight: 700, fontSize: '0.875rem', cursor: 'pointer' };
const outlineBtn = { background: 'white', color: '#1a7a4a', border: '1.5px solid #1a7a4a', borderRadius: 10, padding: '0.6rem 1.1rem', fontWeight: 700, fontSize: '0.875rem', cursor: 'pointer' };
const input = { padding: '0.55rem 0.75rem', border: '1.5px solid #e0e0e0', borderRadius: 8, fontSize: '0.875rem', fontFamily: 'inherit', outline: 'none', background: 'white' };
const labelStyle = { display: 'flex', flexDirection: 'column', gap: '0.3rem', fontSize: '0.8rem', fontWeight: 600, color: '#444' };
const grid2 = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.9rem' };
const sectionLabel = { gridColumn: '1/-1', fontWeight: 800, fontSize: '0.8rem', color: '#1a7a4a', textTransform: 'uppercase', letterSpacing: '0.05em', paddingBottom: '0.3rem', borderBottom: '2px solid #e8f5e9' };
const platformHeader = (color) => ({ fontWeight: 800, fontSize: '0.85rem', color: color, marginBottom: '0.75rem', marginTop: '0.5rem', paddingBottom: '0.3rem', borderBottom: `2px solid ${color}22` });
